import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const RecentDistributionsTable = ({ distributions, onViewDetails }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getMethodBadgeClass = (method) => {
    switch (method) {
      case "Percentage by Role":
        return "bg-primary/10 text-primary";
      case "Equal Split":
        return "bg-secondary/10 text-secondary";
      case "Custom":
        return "bg-accent/10 text-accent";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusConfig = (status, isLocked, hasManualOverride) => {
    const baseConfig = {
      Completed: {
        icon: "CheckCircle2",
        color: "var(--color-success)",
        text: "Completed",
      },
      Approved: {
        icon: "CheckCircle",
        color: "var(--color-primary)",
        text: "Approved",
      },
      Pending: {
        icon: "Clock",
        color: "var(--color-warning)",
        text: "Pending",
      },
      Draft: {
        icon: "FileEdit",
        color: "var(--color-muted-foreground)",
        text: "Draft",
      },
    };

    const config = baseConfig[status] || baseConfig.Pending;

    if (isLocked) {
      config.locked = true;
    }

    return config;
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="w-full overflow-x-auto">
          <table className="table w-full min-w-[1100px]">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-caption font-semibold text-foreground">
                  Date
                </th>
                <th className="text-right px-6 py-4 text-sm font-caption font-semibold text-foreground">
                  Amount
                </th>
                <th className="text-center px-6 py-4 text-sm font-caption font-semibold text-foreground">
                  Employees
                </th>
                <th className="text-left px-6 py-4 text-sm font-caption font-semibold text-foreground">
                  Method
                </th>
                <th className="text-center px-6 py-4 text-sm font-caption font-semibold text-foreground">
                  Status
                </th>
                <th className="text-right px-6 py-4 text-sm font-caption font-semibold text-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {distributions?.map((dist) => {
                const statusConfig = getStatusConfig(
                  dist?.status,
                  dist?.isLocked,
                  dist?.hasManualOverride,
                );
                const isLocked = dist?.isLocked;

                return (
                  <tr
                    key={dist?.id}
                    className={`border-t border-border transition-base cursor-pointer ${
                      isLocked
                        ? "bg-muted/30 hover:bg-muted/40"
                        : "hover:bg-muted/30"
                    }`}
                    onClick={() => onViewDetails(dist?.id)}
                  >
                    <td
                      className={`px-6 py-4 text-sm font-caption ${
                        isLocked ? "text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isLocked && (
                          <Icon
                            name="Lock"
                            size={14}
                            className="text-muted-foreground"
                          />
                        )}
                        {dist?.date}
                        {dist?.locationName && (
                          <span className="text-xs text-muted-foreground">
                            ({dist.locationName})
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 text-sm font-data font-semibold text-right whitespace-nowrap ${
                        isLocked ? "text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {dist?.amount}
                    </td>
                    <td
                      className={`px-6 py-4 text-sm font-caption text-center ${
                        isLocked ? "text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {dist?.employeeCount}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-caption font-semibold ${getMethodBadgeClass(
                          dist?.method,
                        )} ${isLocked ? "opacity-60" : ""}`}
                      >
                        {dist?.method}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Icon
                          name={statusConfig.icon}
                          size={16}
                          color={statusConfig.color}
                        />
                        <span
                          className={`text-sm font-caption ${
                            isLocked
                              ? "text-muted-foreground"
                              : "text-foreground"
                          }`}
                        >
                          {statusConfig.text}
                        </span>
                        {dist?.hasManualOverride && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-warning/10 text-warning text-xs rounded-full"
                            title="Manual override applied"
                          >
                            <Icon name="Flag" size={12} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        iconPosition="left"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onViewDetails(dist?.id);
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {distributions?.map((dist) => {
          const statusConfig = getStatusConfig(
            dist?.status,
            dist?.isLocked,
            dist?.hasManualOverride,
          );
          const isLocked = dist?.isLocked;

          return (
            <div
              key={dist?.id}
              className={`p-4 ${isLocked ? "bg-muted/30" : ""}`}
            >
              <div
                className="flex items-start justify-between mb-3 cursor-pointer"
                onClick={() => toggleRow(dist?.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {isLocked && (
                      <Icon
                        name="Lock"
                        size={14}
                        className="text-muted-foreground"
                      />
                    )}
                    <Icon
                      name="Calendar"
                      size={14}
                      color="var(--color-muted-foreground)"
                    />
                    <span
                      className={`text-sm font-caption font-semibold ${
                        isLocked ? "text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {dist?.date}
                    </span>
                    {dist?.hasManualOverride && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-warning/10 text-warning text-xs rounded-full">
                        <Icon name="Flag" size={10} />
                      </span>
                    )}
                  </div>
                  {dist?.locationName && (
                    <p className="text-xs text-muted-foreground mb-1">
                      {dist.locationName}
                    </p>
                  )}
                  <div
                    className={`text-xl md:text-2xl font-data font-bold mb-2 ${
                      isLocked ? "text-muted-foreground" : "text-foreground"
                    }`}
                  >
                    {dist?.amount}
                  </div>
                  <span
                    className={`inline-flex px-2 py-1 rounded-full text-xs font-caption font-semibold ${getMethodBadgeClass(
                      dist?.method,
                    )} ${isLocked ? "opacity-60" : ""}`}
                  >
                    {dist?.method}
                  </span>
                </div>
                <Icon
                  name={expandedRow === dist?.id ? "ChevronUp" : "ChevronDown"}
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </div>

              {expandedRow === dist?.id && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-caption text-muted-foreground">
                      Employees
                    </span>
                    <span
                      className={`text-sm font-caption font-semibold ${
                        isLocked ? "text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {dist?.employeeCount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-caption text-muted-foreground">
                      Status
                    </span>
                    <div className="flex items-center gap-2">
                      <Icon
                        name={statusConfig.icon}
                        size={14}
                        color={statusConfig.color}
                      />
                      <span
                        className={`text-sm font-caption ${
                          isLocked ? "text-muted-foreground" : "text-foreground"
                        }`}
                      >
                        {statusConfig.text}
                      </span>
                    </div>
                  </div>
                  {isLocked && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
                      <Icon name="Lock" size={12} />
                      <span>This record is locked and cannot be modified</span>
                    </div>
                  )}
                  {dist?.hasManualOverride && (
                    <div className="flex items-center gap-2 text-xs text-warning pt-1">
                      <Icon name="Flag" size={12} />
                      <span>Manual override was applied</span>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => onViewDetails(dist?.id)}
                    className="mt-3"
                  >
                    View Details
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentDistributionsTable;
